import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical, Eye, Edit, Trash2, Play, Pause, CheckCircle, Activity, Bot, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { AgentResponse, AgentStatus } from '@/types/api'
import { cn } from '@/lib/utils'

interface AgentListTableProps {
  agents: AgentResponse[]
  loading?: boolean
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onViewDetails: (agent: AgentResponse) => void
  onEdit: (agent: AgentResponse) => void
  onDelete: (agent: AgentResponse) => void
  onActivate: (agent: AgentResponse) => void
  onDeactivate: (agent: AgentResponse) => void
  onValidate: (agent: AgentResponse) => void
  onHealthCheck: (agent: AgentResponse) => void
}

const getStatusDotColor = (status: AgentStatus): string => {
  switch (status) {
    case 'active':
      return 'bg-success'
    case 'pending_validation':
      return 'bg-warning'
    case 'failed_validation':
      return 'bg-destructive'
    case 'inactive':
    default:
      return 'bg-muted-foreground/40'
  }
}

const formatStatus = (status: AgentStatus): string => {
  switch (status) {
    case 'pending_validation':
      return 'Pending'
    case 'failed_validation':
      return 'Failed'
    default:
      return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—'
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return dateString
  }
}

const MENU_WIDTH = 176

export function AgentListTable({
  agents,
  loading = false,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onValidate,
  onHealthCheck,
}: AgentListTableProps) {
  const [menu, setMenu] = useState<{ agentId: string; top: number; left: number } | null>(null)

  useEffect(() => {
    if (!menu) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menu])

  const openMenu = (agent: AgentResponse, el: HTMLElement) => {
    const rect = el.getBoundingClientRect()
    let left = rect.right - MENU_WIDTH
    let top = rect.bottom + 4
    if (left < 8) left = 8
    if (left + MENU_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - MENU_WIDTH - 8
    }
    const estHeight = 280
    if (top + estHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estHeight - 4)
    }
    setMenu({ agentId: agent.agent_id, top, left })
  }

  const menuAgent = menu ? agents.find((a) => a.agent_id === menu.agentId) : null

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1)
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalCount)

  if (loading) {
    return (
      <div className="p-1">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Agent</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Endpoint</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Last Check</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3, 4].map((i) => (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-muted animate-pulse" />
                    <div className="space-y-1.5">
                      <div className="h-3.5 w-24 bg-muted rounded animate-pulse" />
                      <div className="h-2.5 w-16 bg-muted/60 rounded animate-pulse" />
                    </div>
                  </div>
                </TableCell>
                <TableCell><div className="h-5 w-16 bg-muted rounded-full animate-pulse" /></TableCell>
                <TableCell><div className="h-3 w-32 bg-muted rounded animate-pulse" /></TableCell>
                <TableCell><div className="h-3 w-14 bg-muted rounded animate-pulse" /></TableCell>
                <TableCell><div className="h-6 w-6 bg-muted rounded animate-pulse mx-auto" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mb-4">
          <Bot className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-1">No agents found</h3>
        <p className="text-xs text-muted-foreground text-center max-w-xs">
          Register your first agent to get started with the platform.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="p-1 flex-1 min-h-0 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-none">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Agent</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Endpoint</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Last Check</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((agent) => (
              <TableRow
                key={agent.agent_id}
                className="cursor-pointer group"
                onClick={() => onViewDetails(agent)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground">{agent.name}</span>
                        {agent.agent_type && agent.agent_type !== 'generic' && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                            agent.agent_type === 'sql'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : agent.agent_type === 'tool'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {agent.agent_type}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{agent.agent_id}</div>
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${getStatusDotColor(agent.status)}`} />
                    <span className="text-sm text-foreground">{formatStatus(agent.status)}</span>
                  </div>
                </TableCell>

                <TableCell>
                  <span className="text-xs text-muted-foreground font-mono">
                    {agent.base_url}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(agent.last_health_check)}
                  </span>
                </TableCell>

                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-haspopup="menu"
                    aria-expanded={menu?.agentId === agent.agent_id}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (menu?.agentId === agent.agent_id) {
                        setMenu(null)
                      } else {
                        openMenu(agent, e.currentTarget)
                      }
                    }}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2.5 border-t border-border bg-muted/20 text-xs text-muted-foreground flex-shrink-0">
        <div>
          Showing <span className="font-medium text-foreground">{from}</span>–
          <span className="font-medium text-foreground">{to}</span> of{' '}
          <span className="font-medium text-foreground">{totalCount}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5">
            <span className="hidden sm:inline">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value))
                onPageChange(1)
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="tabular-nums px-1">
              {page} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {menu &&
        menuAgent &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              aria-hidden
              onClick={() => setMenu(null)}
            />
            <div
              role="menu"
              className={cn(
                'fixed z-[101] w-44 rounded-lg border bg-popover text-popover-foreground shadow-lg py-1',
              )}
              style={{ top: menu.top, left: menu.left, width: MENU_WIDTH }}
            >
              <button
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors text-left"
                onClick={() => {
                  onViewDetails(menuAgent)
                  setMenu(null)
                }}
              >
                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                View Details
              </button>
              <button
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors text-left"
                onClick={() => {
                  onEdit(menuAgent)
                  setMenu(null)
                }}
              >
                <Edit className="h-3.5 w-3.5 text-muted-foreground" />
                Edit
              </button>
              {menuAgent.status === 'active' ? (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors text-left"
                  onClick={() => {
                    onDeactivate(menuAgent)
                    setMenu(null)
                  }}
                >
                  <Pause className="h-3.5 w-3.5 text-muted-foreground" />
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors text-left"
                  onClick={() => {
                    onActivate(menuAgent)
                    setMenu(null)
                  }}
                >
                  <Play className="h-3.5 w-3.5 text-muted-foreground" />
                  Activate
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors text-left"
                onClick={() => {
                  onValidate(menuAgent)
                  setMenu(null)
                }}
              >
                <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" />
                Validate
              </button>
              <button
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent rounded-sm transition-colors text-left"
                onClick={() => {
                  onHealthCheck(menuAgent)
                  setMenu(null)
                }}
              >
                <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                Health Check
              </button>
              <div className="my-1 h-px bg-border mx-2" />
              <button
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-sm transition-colors text-left"
                onClick={() => {
                  onDelete(menuAgent)
                  setMenu(null)
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </>,
          document.body,
        )}
    </div>
  )
}
